import { useState, useRef, useEffect } from "react";
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';

import { updateProject, deleteProject, createProjectSite, updateProjectSite } from "../../api/projects";
import ConfirmationDialog from "../ConfirmationDialog";
import { autocompleteAddress, getPlaceDetails, GoogleAddressSchema } from "../../api/address";

import { 
  FaCog,
  FaFileAlt,
  FaMapMarkerAlt,
  FaTrash,
  FaExpand,
  FaCompress,
  FaCircle,
  FaPlus,
  FaSave
} from 'react-icons/fa';

import { getTableHeaderStyle, getBadgeTailwindClasses } from '../../styles/designSystem';

interface ProjectDescriptionProps {
  projectData: any;
  projectId: string;
  onProjectUpdate?: () => void;
}

interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export const ProjectDescription = ({ projectData, projectId, onProjectUpdate }: ProjectDescriptionProps) => {
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;
  const navigate = useNavigate();

  const [description, setDescription] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sites, setSites] = useState<any[]>([]);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editingSiteName, setEditingSiteName] = useState('');
  const [editingSiteAddress, setEditingSiteAddress] = useState('');
  const [isSavingSite, setIsSavingSite] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);
  const [selectedAddressData, setSelectedAddressData] = useState<GoogleAddressSchema | null>(null);
  
  // Description edit confirmation
  const [showDescriptionEditConfirmation, setShowDescriptionEditConfirmation] = useState(false);
  const [pendingDescriptionChange, setPendingDescriptionChange] = useState('');
  
  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      // Scroll effect removed
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  // Initialize address from project data
  useEffect(() => {
    console.log('🏗️ Project data received:', projectData);
    if (projectData.sites && projectData.sites.length > 0) {
      console.log('📍 Sites from project data:', projectData.sites);
      console.log('🔍 Checking is_default flags:', projectData.sites.map((site: any) => ({ 
        name: site.name, 
        id: site.id, 
        is_default: site.is_default,
        full_site_data: site
      })));
      
      // Check if we have local state that should be preserved
      const hasLocalDefaultSite = sites.some(site => site.is_default === true);
      console.log('🔍 Has local default site:', hasLocalDefaultSite);
      
      if (hasLocalDefaultSite) {
        console.log('⚠️ Preserving local state with default site');
        // Don't overwrite local state if we have a default site
        return;
      }
      
      // Store all sites
      setSites(projectData.sites);
    }
  }, [projectData.sites]);

  // Initialize description
  useEffect(() => {
    setDescription(projectData.description || '');
  }, [projectData.description]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [description, isExpanded]);

  // Address autocomplete with debouncing
  useEffect(() => {
    if (!editingSiteId) return;

    const delayDebounceFn = setTimeout(async () => {
      if (editingSiteAddress.length >= 3 && token) {
        setIsLoadingAddressSuggestions(true);
        try {
          const result = await autocompleteAddress(token, editingSiteAddress);
          setAddressSuggestions(result.predictions || []);
          setShowAddressSuggestions(true);
        } catch (error) {
          console.error('Error fetching address suggestions:', error);
          setAddressSuggestions([]);
        } finally {
          setIsLoadingAddressSuggestions(false);
        }
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [editingSiteAddress, token, editingSiteId]);

  // Handle click outside address suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(event.target as Node) &&
          addressInputRef.current && !addressInputRef.current.contains(event.target as Node)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveDescription = async (newDescription: string) => {
    if (!token || !projectId) {
      toast.error('Authentication required');
      return;
    }

    setIsSaving(true);
    try {
      await updateProject(token, projectId, {
        ...projectData,
        description: newDescription
      });
      toast.success('Description updated successfully');
    } catch (error) {
      console.error('Error updating description:', error);
      toast.error('Failed to update description');
      // Revert to original value on error
      setDescription(projectData.description || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Only save if description actually changed
    if (description !== (projectData.description || '')) {
      setPendingDescriptionChange(description);
      setShowDescriptionEditConfirmation(true);
    }
  };

  const confirmDescriptionChange = async () => {
    await saveDescription(pendingDescriptionChange);
    setShowDescriptionEditConfirmation(false);
    setPendingDescriptionChange('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      textareaRef.current?.blur();
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelect = async (suggestion: AddressSuggestion) => {
    if (!token) return;

    setIsLoadingAddressSuggestions(true);
    try {
      const placeDetails = await getPlaceDetails(token, suggestion.place_id);
      setSelectedAddressData(placeDetails);
      setEditingSiteAddress(placeDetails.formatted_address);
      setShowAddressSuggestions(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
      toast.error('Failed to load address details');
    } finally {
      setIsLoadingAddressSuggestions(false);
    }
  };

  // Site management functions
  const handleEditSite = (site: any) => {
    setEditingSiteId(site.id);
    setEditingSiteName(site.name || '');
    setEditingSiteAddress(site.google_address_data?.formatted_address || site.address || '');
    // Reset autocomplete state
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setSelectedAddressData(null);
  };

  const handleCancelEditSite = () => {
    setEditingSiteId(null);
    setEditingSiteName('');
    setEditingSiteAddress('');
    // Reset autocomplete state
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setSelectedAddressData(null);
  };

  const handleSaveSite = async () => {
    if (!token || !projectId || !editingSiteId) {
      toast.error('Authentication required');
      return;
    }

    setIsSavingSite(true);
    try {
      const siteToUpdate = sites.find(s => s.id === editingSiteId);
      if (!siteToUpdate) {
        toast.error('Site not found');
        return;
      }

      const updatedSite = {
        ...siteToUpdate,
        name: editingSiteName,
        address: editingSiteAddress,
          google_address_data: selectedAddressData ? {
            ...selectedAddressData,
          formatted_address: editingSiteAddress
        } : siteToUpdate.google_address_data ? {
          ...siteToUpdate.google_address_data,
          formatted_address: editingSiteAddress
          } : {
          formatted_address: editingSiteAddress,
            latitude: 0,
            longitude: 0,
            place_id: ''
          }
        };
        
      await updateProjectSite(token, projectId, editingSiteId, updatedSite);
      
      // Update local state
      setSites(sites.map(s => s.id === editingSiteId ? updatedSite : s));
      
      toast.success('Site updated successfully');
      setEditingSiteId(null);
      setEditingSiteName('');
      setEditingSiteAddress('');
      // Reset autocomplete state
      setShowAddressSuggestions(false);
      setAddressSuggestions([]);
      setSelectedAddressData(null);
    } catch (error) {
      console.error('Error updating site:', error);
      toast.error('Failed to update site');
    } finally {
      setIsSavingSite(false);
    }
  };

  const handleAddSite = async () => {
    if (!token || !projectId) {
      toast.error('Authentication required');
      return;
    }

    setIsSavingSite(true);
    try {
      const newSite = {
        name: 'New Site',
        address: '',
        geo_location: [0, 0] as [number, number], // This state was removed
        project_id: projectId
      } as any; // Use any to bypass the id requirement for creation

      const createdSite = await createProjectSite(token, projectId, newSite);
      
      // Update local state
      setSites([...sites, createdSite]);
      
      toast.success('Site added successfully');
    } catch (error) {
      console.error('Error creating site:', error);
      toast.error('Failed to create site');
    } finally {
      setIsSavingSite(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!token || !projectId) {
      toast.error('Authentication required');
      return;
    }

    setIsDeleting(true);
    try {
      console.log('🗑️ Deleting project:', projectData.name);
      await deleteProject(token, projectId);
      toast.success(`Project "${projectData.name}" deleted successfully`);
      setShowDeleteModal(false);
      // Navigate back to projects list
      navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getDescriptionStatus = () => {
    if (isSaving) return { label: 'Saving...', color: '#F59E0B' };
    if (description === (projectData.description || '')) return { label: 'Saved', color: '#10B981' };
    return { label: 'Unsaved changes', color: '#EF4444' };
  };

  const status = getDescriptionStatus();
  const wordCount = description?.trim().split(/\s+/).filter((word: string) => word.length > 0).length || 0;
  const charCount = description?.length || 0;

  // Добавить функцию для смены дефолтного сайта
  const makeDefaultSite = async (siteId: string) => {
    if (!token) return;
    try {
      // Find the current site to get all its data
      const currentSite = sites.find(site => site.id === siteId);
      if (!currentSite) {
        toast.error('Site not found');
        return;
      }

      // Send all required fields along with is_default
      await updateProjectSite(token, projectId, siteId, {
        id: currentSite.id,
        name: currentSite.name,
        description: currentSite.description || '',
        address: currentSite.address || '',
        geo_location: Array.isArray(currentSite.geo_location) 
          ? currentSite.geo_location
          : currentSite.geo_location.split(',').map((coord: string) => parseFloat(coord)),
        project_id: projectId,
        google_address_data: currentSite.google_address_data,
        is_default: true
      });
      
      toast.success('Default site updated');
      console.log('🔄 Updating local state for site:', siteId);
      console.log('📋 Current sites before update:', sites);
      
      // Update local state
      setSites((prev) => {
        const updated = prev.map(site => ({
          ...site,
          is_default: site.id === siteId
        }));
        console.log('📋 Updated sites:', updated);
        return updated;
      });
      
      // Also update projectData if it has sites
      if (projectData && projectData.sites) {
        console.log('🔄 Updating projectData sites as well');
        // Note: This is a read-only prop, so we can't directly update it
        // The parent component should handle this update
      }
      
      // Notify parent component to refresh project data
      if (onProjectUpdate) {
        console.log('🔄 Calling onProjectUpdate to refresh data');
        onProjectUpdate();
      }
    } catch (e: any) {
      toast.error('Failed to update default site');
      console.error('Error updating default site:', e);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white border border-gray-200 rounded-lg transition-shadow duration-200`}>
            {/* Header Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Title */}
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3">
                    <FaCog className="text-blue-600 w-5 h-5" />
                    <h2 className="text-xl font-semibold" style={{ fontFamily: '"Arcadia Display", system-ui, sans-serif' }}>
                      Project Settings
                    </h2>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="flex-1">
                  Manage project description, address, and project lifecycle settings
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Content - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white p-6 space-y-8">
          
          {/* Description Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FaFileAlt className="text-blue-600 w-5 h-5" />
                <h3 className="text-lg font-semibold">Description</h3>
                  
                  {/* Status Indicator */}
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      {/* Rotating Ring when saving */}
                      {isSaving && (
                        <div className={`absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin border-t-blue-500/60 border-r-blue-500/60`} style={{ animation: 'spin 3s linear infinite' }} />
                      )}
                      {/* Status Dot */}
                      <FaCircle 
                        className="w-2 h-2" 
                        style={{ color: status.color }}
                      />
                    </div>
                    <span className="text-sm text-gray-500">{status.label}</span>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{wordCount} words</span>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span>{charCount} characters</span>
                  </div>
                </div>

                  {/* Expand/Collapse */}
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      isExpanded 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    {isExpanded ? <FaCompress className="w-3.5 h-3.5" /> : <FaExpand className="w-3.5 h-3.5" />}
                    <span>{isExpanded ? 'Compact' : 'Expand'}</span>
                  </button>
            </div>

            {/* Description Editor */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="Enter a comprehensive project description including goals, scope, requirements, and timeline..."
                className={`w-full p-4 border rounded-lg resize-none font-mono text-sm leading-relaxed transition-all duration-200 ${
                  isFocused 
                    ? 'border-blue-500 bg-blue-50/30 ring-2 ring-blue-100' 
                    : 'border-gray-300 bg-gray-50/50 hover:border-gray-400'
                }`}
                style={{
                  minHeight: isExpanded ? '500px' : '200px',
                  fontSize: '13px',
                  lineHeight: '1.5'
                }}
              />

              {/* Keyboard Shortcut Hint */}
              {isFocused && (
                <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-white/80 px-2 py-1 rounded">
                  Press Ctrl+Enter to save
                </div>
              )}
            </div>

            {/* Save Status */}
            {isSaving && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
                <div className="animate-spin rounded-full h-3 w-3 border border-blue-300 border-t-blue-600"></div>
                <span>Saving description...</span>
              </div>
            )}
                     </div>

          {/* Divider */}
          <div className="border-t border-gray-200"></div>

          {/* Addresses Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FaMapMarkerAlt className="text-green-600 w-5 h-5" />
                <h3 className="text-lg font-semibold">Addresses <span className={getBadgeTailwindClasses('dev')}>dev</span></h3>
                   </div>
              
                <button
                onClick={handleAddSite}
                disabled={isSavingSite}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <FaPlus className="w-4 h-4" />
                <span>Add Site</span>
                </button>
                 </div>
            
            {/* Sites Content - List */}
            <div className="space-y-4">
              {sites.map((site, index) => (
                <div key={site.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  {editingSiteId === site.id ? (
                    // Edit Mode
              <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Site Name
                        </label>
                        <input
                          type="text"
                          value={editingSiteName}
                          onChange={(e) => setEditingSiteName(e.target.value)}
                          placeholder="Enter site name..."
                          className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address
                        </label>
                <div className="relative">
                  <input
                    ref={addressInputRef}
                    type="text"
                            value={editingSiteAddress}
                    onChange={(e) => {
                              setEditingSiteAddress(e.target.value);
                      // Clear selectedAddressData when user types manually
                      if (selectedAddressData && selectedAddressData.formatted_address !== e.target.value) {
                        setSelectedAddressData(null);
                      }
                    }}
                            placeholder="Enter site address..."
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                  
                  {/* Loading indicator */}
                  {isLoadingAddressSuggestions && (
                            <div className="absolute right-3 top-3">
                      <div className="animate-spin rounded-full h-4 w-4 border border-gray-300 border-t-blue-600"></div>
               </div>
            )}

                  {/* Address suggestions dropdown */}
                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div 
                      ref={addressSuggestionsRef}
                      className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                    >
                      {addressSuggestions.map((suggestion, index) => (
                        <button
                          key={suggestion.place_id || index}
                          onClick={() => handleAddressSelect(suggestion)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-start space-x-3">
                            <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {suggestion.structured_formatting.main_text}
                              </p>
                              <p className="text-xs text-gray-500">
                                {suggestion.structured_formatting.secondary_text}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                  )}
                        </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                          onClick={handleCancelEditSite}
                          disabled={isSavingSite}
                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                          onClick={handleSaveSite}
                          disabled={isSavingSite || !editingSiteName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                          {isSavingSite ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                              <FaSave className="w-4 h-4" />
                        <span>Save</span>
                      </>
                    )}
                  </button>
                </div>
                  </div>
            ) : (
                    // View Mode
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                  <FaMapMarkerAlt className="text-gray-400 w-4 h-4 mt-1" />
                        <div className="flex-1">
                          {/* Site Name */}
                          <div className="mb-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {site.name || `Site ${index + 1}`}
                            </span>
                            {console.log('🔍 Rendering site:', site.name, 'is_default:', site.is_default)}
                            {site.is_default && (
                              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">Default</span>
                            )}
                            {!site.is_default && (
                              <button
                                onClick={() => makeDefaultSite(site.id)}
                                className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded hover:bg-yellow-100 hover:text-yellow-800"
                              >
                                Make Default
                              </button>
                            )}
                          </div>
                          {/* Address */}
                    <p className="text-gray-900 font-medium" style={{
                      fontSize: '13px',
                      fontFamily: '"Arcadia Text", system-ui, sans-serif',
                      color: '#374151',
                      lineHeight: '1.3'
                    }}>
                            {site.google_address_data?.formatted_address || site.address || 'No address specified'}
                    </p>

                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleEditSite(site)}
                        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>Edit</span>
                      </button>
                  </div>
                  )}
                </div>
              ))}
              
              {sites.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <FaMapMarkerAlt className="text-gray-400 w-8 h-8 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">No sites added yet</p>
                  <button
                    onClick={handleAddSite}
                    disabled={isSavingSite}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2 mx-auto"
                  >
                    <FaPlus className="w-4 h-4" />
                    <span>Add First Site</span>
                  </button>
              </div>
            )}
              </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200"></div>

          {/* Danger Zone - Delete Project */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <FaTrash className="text-red-600 w-5 h-5" />
              <h3 className="text-lg font-semibold text-red-900">Danger Zone <span className={getBadgeTailwindClasses('dev')}>dev</span></h3>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-red-900 font-medium">Delete Project</h4>
                  <p className="text-sm text-red-700 mt-1">
                    This action will permanently delete the project and all its data. This action cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(true);
                  }}
                  className="ml-4 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Project</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {/* Delete Project Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={handleDeleteProject}
        title="⚠️ Delete Project - DANGER ZONE"
        message={`You are about to permanently delete "${projectData.name}". This action will destroy ALL project data and cannot be undone.`}
        confirmText="Delete Project Forever"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        requireTextConfirmation={true}
        textToConfirm={projectData.name}
        warningItems={[
          "Project data and settings",
          "Assets and materials", 
          "Meeting notes and documents",
          "Team assignments and tasks",
          "All rooms, racks, and equipment data",
          "User access and permissions"
        ]}
        isLoading={isDeleting}
      />

      {/* Description Edit Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDescriptionEditConfirmation}
        onClose={() => {
          setShowDescriptionEditConfirmation(false);
          setPendingDescriptionChange('');
          // Reset description to original value
          setDescription(projectData.description || '');
        }}
        onConfirm={confirmDescriptionChange}
        title="Confirm Project Description Change"
        message="Are you sure you want to update the project description?"
        confirmText="Update Description"
        cancelText="Cancel"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700"
        warningItems={[
          "This will update the project description for all team members",
          "The change will be visible to everyone with access to this project"
        ]}
        isLoading={isSaving}
      />
    </div>
  );
}; 